import React, { useState } from 'react';
import { cn } from '@/lib/utils';

const actions = ['Inquiry details organized', 'Approved service information checked', 'Response draft prepared'];

const summary = [
  ['Type', 'New service inquiry'],
  ['Intent', 'Wants next steps'],
  ['Missing', 'Service of interest'],
  ['Route to', 'Sales owner'],
];

/**
 * Hero visual: a static example of one assistant's observable actions and
 * outputs. It deliberately avoids an "AI thinking" animation — the point is
 * what the assistant produced and where a person reviews it.
 */
const AssistantWorkbench = () => {
  const [open, setOpen] = useState(false);

  return (
    <figure aria-label="Example assistant workflow" className="surface surface-lit w-full overflow-hidden">
      <div className="flex items-end justify-between gap-4 border-b border-rule px-6 py-5">
        <div>
          <p className="eyebrow">Assistant workbench</p>
          <h2 className="mt-1 text-[1.4rem] leading-tight">Customer Inquiry Assistant</h2>
        </div>
        <p className="text-right font-mono text-[0.62rem] uppercase leading-[1.7] tracking-[0.14em] text-mist">
          <span className="gold-shine">Example</span> workflow
          <br />
          GoldCrusade
        </p>
      </div>

      <div className="px-6 pb-6 pt-5">
        <p className="meta-label mb-2.5">Incoming request</p>
        <blockquote className="border-l-2 border-goldcore/60 pl-4 font-serif text-xl leading-snug text-softwhite">
          “I’m interested in your services. What happens next?”
        </blockquote>

        <p className="meta-label mb-2 mt-6">Actions</p>
        <ul className="font-mono text-[0.72rem] leading-[1.7]">
          {actions.map((action, i) => (
            <li
              key={action}
              className="grid grid-cols-[2.5rem_1fr_auto] gap-3 border-b border-dashed border-rule py-2 last:border-b-0"
            >
              <span className="text-mist">0{i + 1}</span>
              <span>{action}</span>
              <span className="text-[#9ad07a]">Done</span>
            </li>
          ))}
        </ul>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-rule pt-4">
          <p className="flex items-center gap-2 font-mono text-[0.62rem] uppercase tracking-[0.14em]">
            <span
              aria-hidden="true"
              className="h-3.5 w-3.5 rounded-full shadow-[inset_0_0_0_1px_rgba(0,0,0,0.35)]"
              style={{ background: 'radial-gradient(circle at 40% 30%, #fff0c8, #e9b864 60%, #c89238)' }}
            />
            <span className="text-mist">Status:</span> <span className="gold-shine">Human review required</span>
          </p>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="workbench-draft"
            className="gc-btn gc-btn-sm"
          >
            {open ? 'Hide draft' : 'View draft'}
            <span className={cn('gc-arrow', open && 'rotate-90')} aria-hidden="true">
              →
            </span>
          </button>
        </div>

        {open && (
          <div id="workbench-draft" className="mt-5 grid gap-4 sm:grid-cols-[1.3fr_1fr]">
            <div className="border border-rule bg-knightblack/60 p-4">
              <p className="meta-label mb-2">Sample response · draft</p>
              <p className="text-sm leading-relaxed text-softwhite/90">
                Thanks for reaching out. The next step is a short call to understand what you need. You can pick a time
                using the link below, or reply with two times that suit you and we’ll confirm one.
              </p>
            </div>
            <div className="border border-rule bg-knightblack/60 p-4 text-sm">
              <p className="meta-label mb-3">Inquiry summary</p>
              <dl className="space-y-2">
                {summary.map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-3">
                    <dt className="text-mist">{k}</dt>
                    <dd className="text-right text-softwhite">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        )}
      </div>
    </figure>
  );
};

export default AssistantWorkbench;
